import React, { useState, useEffect, useMemo } from 'react';
import { roomsAPI, productsAPI } from './services/api';
import { validatePhone } from './utils/phoneUtils';
import './RoomsPage.css';

interface RoomsPageProps {
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  children?: number;
  childrenAges?: number[];
}

const RoomsPage: React.FC<RoomsPageProps> = ({ 
  checkIn: initialCheckIn = '', 
  checkOut: initialCheckOut = '', 
  adults: initialAdults = 2,
  children: initialChildren = 0,
  childrenAges: initialChildrenAges
}) => {
  const [checkInDate, setCheckInDate] = useState(initialCheckIn);
  const [checkOutDate, setCheckOutDate] = useState(initialCheckOut);
  const [adultsCount, setAdultsCount] = useState(initialAdults.toString());
  const [childrenCount, setChildrenCount] = useState(initialChildren);
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [searched, setSearched] = useState(false);

  // Yemek seçenekleri
  const [includeBreakfast, setIncludeBreakfast] = useState(false);
  const [includeDinner, setIncludeDinner] = useState(false);
  const [mealPricesLoaded, setMealPricesLoaded] = useState(false);
  const [dinnerClosedDays, setDinnerClosedDays] = useState<number[]>([1]);
  const [childrenAges, setChildrenAges] = useState<number[]>(
    initialChildrenAges && initialChildrenAges.length > 0
      ? initialChildrenAges
      : initialChildren > 0 ? Array(initialChildren).fill(7) : []
  );
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  // Extra Bed Warning Modal State
  const [showExtraBedWarning, setShowExtraBedWarning] = useState(false);
  const [pendingRoom, setPendingRoom] = useState<any>(null);

  // Yemek fiyatları — HER ZAMAN backend API'den çekilir
  const [mealPrices, setMealPrices] = useState({
    breakfastAdult: 0, breakfastChild7_12: 0, breakfastChild0_6: 0,
    dinnerAdult: 0, dinnerChild7_12: 0, dinnerChild0_6: 0
  });
  const [mealPriceError, setMealPriceError] = useState(false);

  // Backend API'den yemek fiyatlarını yükle
  useEffect(() => {
    (async () => {
      try {
        const [breakfastRes, dinnerRes, dinnerPublicRes] = await Promise.all([
          productsAPI.getPublicSetting('lodging_breakfast'),
          productsAPI.getByType('dinner'),
          productsAPI.getPublicSetting('dinner_public').catch(() => null)
        ]);
        const prices = { ...mealPrices };
        const bfData = breakfastRes?.data?.data;
        if (bfData?.price_rules) {
          prices.breakfastAdult = bfData.price_rules.adult || 0;
          prices.breakfastChild7_12 = bfData.price_rules.child_7_12 || 0;
          prices.breakfastChild0_6 = bfData.price_rules.child_0_6 || 0;
        }
        const dnData = dinnerRes?.data?.data;
        if (dnData?.price_rules) {
          prices.dinnerAdult = dnData.price_rules.adult || 0;
          prices.dinnerChild7_12 = dnData.price_rules.child_7_12 || 0;
          prices.dinnerChild0_6 = dnData.price_rules.child_0_6 || 0;
        }
        
        // Kritik kontrol: Fiyatlar yüklenmediyse hata ver
        if (!prices.dinnerAdult || !prices.breakfastAdult) {
          throw new Error('Kritik fiyat bilgileri eksik! Dinner: ' + prices.dinnerAdult + ', Breakfast: ' + prices.breakfastAdult);
        }
        
        setMealPrices(prices);
        setMealPricesLoaded(true);
        setMealPriceError(false);

        const dinnerPublicValue = dinnerPublicRes?.data?.data?.value;
        if (dinnerPublicValue) {
          const normalizedDays: number[] = Array.isArray(dinnerPublicValue.closed_days)
            ? Array.from(new Set<number>(dinnerPublicValue.closed_days
                .map((d: any) => Number(d))
                .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)))
                .sort((a, b) => a - b)
            : [1];
          setDinnerClosedDays(normalizedDays.length > 0 ? normalizedDays : [1]);
        }
      } catch (err) {
        console.error('❌ HATA: Yemek fiyatları yüklenemedi! Rezervasyon sistemi devre dışı.', err);
        setMealPriceError(true);
        setMealPricesLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Geceleme sayısı
  const nights = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0;
    const ci = new Date(checkInDate);
    const co = new Date(checkOutDate);
    const diff = Math.ceil((co.getTime() - ci.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }, [checkInDate, checkOutDate]);

  // Kapalı gün hesaplaması
  const excludedNights = useMemo(() => {
    if (!checkInDate || nights <= 0) return { breakfasts: 0, dinners: 0 };
    let excludedBreakfasts = 0;
    let excludedDinners = 0;
    for (let i = 0; i < nights; i++) {
      const d = new Date(checkInDate);
      d.setDate(d.getDate() + i);
      if (dinnerClosedDays.includes(d.getDay())) excludedDinners++;
      const next = new Date(d); next.setDate(d.getDate() + 1);
      if (dinnerClosedDays.includes(next.getDay())) excludedBreakfasts++;
    }
    return { breakfasts: excludedBreakfasts, dinners: excludedDinners };
  }, [checkInDate, nights, dinnerClosedDays]);

  const billableBreakfastNights = Math.max(nights - excludedNights.breakfasts, 0);
  const billableDinnerNights = Math.max(nights - excludedNights.dinners, 0);

  // Yemek fiyat hesaplama
  const mealCosts = useMemo(() => {
    if (!selectedRoom || nights <= 0) return { breakfast: 0, dinner: 0, total: 0 };
    const children0_6 = childrenAges.filter(a => a >= 0 && a <= 6).length;
    const children7_12 = childrenAges.filter(a => a >= 7 && a <= 12).length;
    const teenAdults = childrenAges.filter(a => a >= 13).length;
    const effectiveAdults = parseInt(adultsCount) + teenAdults;
    let breakfastCost = 0;
    let dinnerCost = 0;
    if (includeBreakfast) {
      breakfastCost = billableBreakfastNights * (effectiveAdults * mealPrices.breakfastAdult + children7_12 * mealPrices.breakfastChild7_12 + children0_6 * mealPrices.breakfastChild0_6);
    }
    if (includeDinner) {
      dinnerCost = billableDinnerNights * (effectiveAdults * mealPrices.dinnerAdult + children7_12 * mealPrices.dinnerChild7_12 + children0_6 * mealPrices.dinnerChild0_6);
    }
    return { breakfast: breakfastCost, dinner: dinnerCost, total: breakfastCost + dinnerCost };
  }, [selectedRoom, nights, adultsCount, childrenAges, includeBreakfast, includeDinner, billableBreakfastNights, billableDinnerNights, mealPrices]);

  // Müsait odaları ara
  const searchAvailability = async () => {
    if (!checkInDate || !checkOutDate) {
      setError('Lütfen giriş ve çıkış tarihlerini seçiniz');
      return;
    }

    setLoading(true);
    setError('');
    setSearched(true);
    
    try {
      const response = await roomsAPI.getAvailability({
        checkIn: checkInDate,
        checkOut: checkOutDate,
        adults: Number(adultsCount),
        children: Number(childrenCount),
        childrenAges: childrenAges.join(',')
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setAvailableRooms(response.data.data);
      } else {
        setError('Seçilen tarihler için müsait oda bulunamadı.');
        setAvailableRooms([]);
      }
    } catch (err: any) {
      console.error('Oda müsaitliği hatası:', err);
      setError('Oda müsaitliği sorgulanırken bir hata oluştu. Lütfen tekrar deneyiniz.');
      setAvailableRooms([]);
    } finally {
      setLoading(false);
    }
  };

  // İlk yüklemede eğer tarihler varsa ara
  useEffect(() => {
    if (checkInDate && checkOutDate) {
      searchAvailability();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoomSelect = (room: any) => {
    // Check if extra bed is needed - use standardCapacity (normal comfort capacity)
    const totalGuests = Number(adultsCount) + Number(childrenCount);
    const standardCap = room.standardCapacity || (room.maxOccupancy ? room.maxOccupancy - 1 : 3);
    
    console.log('🔍 Capacity Check (RoomsPage):', { 
      adultsCount, 
      childrenCount, 
      totalGuests, 
      standardCapacity: standardCap,
      maxOccupancy: room.maxOccupancy,
      needsExtraBed: totalGuests > standardCap 
    });
    
    if (totalGuests > standardCap) {
      // Show extra bed warning modal
      setPendingRoom(room);
      setShowExtraBedWarning(true);
    } else {
      // Proceed directly
      proceedWithBooking(room);
    }
  };
  
  const proceedWithBooking = (room: any) => {
    setSelectedRoom(room);
    setIncludeBreakfast(false);
    setIncludeDinner(false);
    setShowBookingForm(true);
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRoom) return;

    try {
      setLoading(true);
      const formData = new FormData(e.target as HTMLFormElement);
      
      // Toplam fiyatı hesapla = oda + yemekler
      const roomPrice = selectedRoom.price || 0;
      const totalPrice = roomPrice + mealCosts.total;
      
      const bookingData: any = {
        roomTypeId: selectedRoom.roomTypeId || selectedRoom.id,
        roomTypeName: selectedRoom.roomTypeName || selectedRoom.name || '',
        checkIn: checkInDate,
        checkOut: checkOutDate,
        adults: parseInt(adultsCount),
        children: childrenCount,
        childrenAges: childrenAges,
        guestName: formData.get('guestName') as string,
        guestEmail: formData.get('guestEmail') as string,
        guestPhone: '',
        specialRequests: formData.get('specialRequests') as string,
        totalPrice,
        includeBreakfast,
        includeDinner,
        breakfastCost: mealCosts.breakfast,
        dinnerCost: mealCosts.dinner
      };

      // Telefon validasyonu
      const rawPhone = formData.get('guestPhone') as string;
      const phoneValidation = validatePhone(rawPhone);
      if (!phoneValidation.valid) {
        alert(`Telefon numarası hatalı: ${phoneValidation.error}`);
        setLoading(false);
        return;
      }
      bookingData.guestPhone = phoneValidation.formatted;

      const response = await roomsAPI.book(bookingData);

      if (response.data.success) {
        // PayTR iframe'ini aç
        const { iframeUrl, reservationId } = response.data.data;
        
        if (iframeUrl) {
          // PayTR ödeme sayfasına yönlendir
          window.location.href = iframeUrl;
        } else {
          alert('Rezervasyon oluşturuldu: ' + reservationId);
          setShowBookingForm(false);
          setSelectedRoom(null);
        }
      }
    } catch (err: any) {
      alert('Rezervasyon oluşturulamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rooms-page">
      <div 
        className="rooms-header"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/hk%20suit%201.jpg')`
        }}
      >
        <h1>Odalarımız</h1>
        <p>Villa Polikne'de sadeliğin ve temizliğin keyifini çıkarın</p>
      </div>

      <div className="container">
        {/* Arama Formu */}
        <div className="search-box">
          <h3>🔍 Oda Arama</h3>
          <div className="form-row">
            <div className="form-group">
              <label>📅 Giriş Tarihi</label>
              <input
                type="date"
                className="form-input"
                value={checkInDate}
                onChange={(e) => {
                  setCheckInDate(e.target.value);
                  // Tarih değişti - eski fiyatlar geçersiz
                  if (searched) {
                    setAvailableRooms([]);
                    setError('Tarih değişti. Lütfen tekrar arama yapınız.');
                  }
                }}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="form-group">
              <label>📅 Çıkış Tarihi</label>
              <input
                type="date"
                className="form-input"
                value={checkOutDate}
                onChange={(e) => {
                  setCheckOutDate(e.target.value);
                  // Tarih değişti - eski fiyatlar geçersiz
                  if (searched) {
                    setAvailableRooms([]);
                    setError('Tarih değişti. Lütfen tekrar arama yapınız.');
                  }
                }}
                min={checkInDate || new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="form-group">
              <label>👨 Yetişkin</label>
              <select 
                className="form-input"
                value={adultsCount}
                onChange={(e) => {
                  const count = parseInt(e.target.value);
                  setAdultsCount(count.toString());
                  // Misafir sayısı değişti - eski fiyatlar geçersiz
                  if (searched) {
                    setAvailableRooms([]);
                    setError('Misafir sayısı değişti. Lütfen tekrar arama yapınız.');
                  }
                }}
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </div>
            <div className="form-group">
              <label>👶 Çocuk</label>
              <select 
                className="form-input"
                value={childrenCount}
                onChange={(e) => {
                  const count = parseInt(e.target.value);
                  setChildrenCount(count);
                  setChildrenAges(prev => {
                    if (count > prev.length) {
                      return [...prev, ...Array(count - prev.length).fill(7)];
                    } else {
                      return prev.slice(0, count);
                    }
                  });
                  // Misafir sayısı değişti - eski fiyatlar geçersiz
                  if (searched) {
                    setAvailableRooms([]);
                    setError('Misafir sayısı değişti. Lütfen tekrar arama yapınız.');
                  }
                }}
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
            </div>
            {childrenCount > 0 && childrenAges.map((age, i) => (
              <div key={i} className="form-group">
                <label>Ç{i + 1} Yaş</label>
                <select
                  className="form-input"
                  value={age}
                  onChange={(e) => {
                    const newAge = parseInt(e.target.value);
                    setChildrenAges(prev => {
                      const updated = [...prev];
                      updated[i] = newAge;
                      return updated;
                    });
                    // Çocuk yaşı değişti - fiyatlar değişebilir
                    if (searched) {
                      setAvailableRooms([]);
                      setError('Çocuk yaşı değişti. Lütfen tekrar arama yapınız.');
                    }
                  }}
                >
                  {Array.from({ length: 18 }, (_, a) => (
                    <option key={a} value={a}>{a} yaş</option>
                  ))}
                </select>
              </div>
            ))}            
            <div className="form-group">
              <button 
                className="btn-primary" 
                onClick={searchAvailability}
                disabled={loading}
              >
                {loading ? '⏳ Aranıyor...' : '🔍 Ara'}
              </button>
            </div>
          </div>
          {error && <p style={{ color: '#e74c3c', marginTop: '1rem', padding: '0.8rem', background: '#fee', borderRadius: '8px' }}>⚠️ {error}</p>}
        </div>

        {/* Ek Yatak Onay Modal */}
        {showExtraBedWarning && pendingRoom && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
            <div className="capacity-warning-modal" style={{ background: '#fff', padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
              <h3 style={{ marginTop: 0, color: '#d4af37', fontSize: '1.5rem', marginBottom: '1rem' }}>⚠️ Önemli Bilgilendirme – Ek Yatak</h3>
              
              <div style={{ background: '#f8f9fa', padding: '1.25rem', borderRadius: '8px', marginBottom: '1.5rem', lineHeight: '1.7', fontSize: '0.95rem' }}>
                <p style={{ marginTop: 0, fontWeight: '600', color: '#333' }}>
                  Seçtiğiniz <strong>{pendingRoom.roomTypeName || pendingRoom.name}</strong> odası için <strong>{adultsCount} yetişkin + {childrenCount} çocuk</strong> (toplam {Number(adultsCount) + Number(childrenCount)} kişi) konaklaması talep ettiniz.
                </p>
                
                <p>Bu odanın <strong>standart kapasitesi {pendingRoom.standardCapacity || (pendingRoom.maxOccupancy ? pendingRoom.maxOccupancy - 1 : 3)} kişi</strong> olup, talep ettiğiniz {Number(adultsCount) + Number(childrenCount)} kişilik konaklama için <strong>ek yatak açılması gerekmektedir.</strong></p>
                
                <p style={{ color: '#856404', fontWeight: '600' }}>Ek yatak açıldığında <strong>oda alanı sınırlı</strong> olacağından konfor standart kapasiteye göre daha düşük olabilir.</p>
                
                <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                
                <p style={{ fontWeight: '600', color: '#d4af37', marginBottom: '0.5rem' }}>Rezervasyonunuza devam ederek;</p>
                <ul style={{ paddingLeft: '1.5rem', marginBottom: 0 }}>
                  <li>Odanın standart kapasitesinin <strong>{pendingRoom.standardCapacity || (pendingRoom.maxOccupancy ? pendingRoom.maxOccupancy - 1 : 3)} kişi</strong> olduğunu,</li>
                  <li>Ek yatağın <strong>istisnai olarak açıldığını</strong>,</li>
                  <li><strong>Oda içi konforun sınırlı olabileceğini</strong>,</li>
                  <li>Bu durumun size önceden bildirildiğini</li>
                </ul>
                <p style={{ marginBottom: 0, marginTop: '0.5rem' }}>kabul etmiş olursunuz.</p>
              </div>
              
              <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #ffc107' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404', fontWeight: '500' }}>
                  ⚠️ Ek yatak nedeniyle oluşabilecek konfor beklentileri sebebiyle yapılacak <strong>iptal veya ücret iadesi talepleri kabul edilmemektedir.</strong>
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button 
                  onClick={() => {
                    setShowExtraBedWarning(false);
                    proceedWithBooking(pendingRoom);
                    setPendingRoom(null);
                  }}
                  style={{ flex: 1, padding: '12px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  ✓ Anladım, Devam Et
                </button>
                <button 
                  onClick={() => {
                    setShowExtraBedWarning(false);
                    setPendingRoom(null);
                  }}
                  style={{ flex: 1, padding: '12px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Vazgeç
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rezervasyon Formu Modal */}
        {showBookingForm && selectedRoom && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="booking-form-modal" style={{ background: '#fff', padding: '30px', borderRadius: '10px', maxWidth: '550px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
              <h3>Rezervasyon Bilgileri</h3>
              <p><strong>{selectedRoom.roomTypeName || selectedRoom.name}</strong></p>
              <p>{checkInDate} - {checkOutDate} ({nights} Gece)</p>
              <p style={{ fontWeight: 'bold', color: '#d4af37' }}>Oda: ₺{(selectedRoom.price || 0).toLocaleString('tr-TR')}</p>

              {/* Yemek Seçenekleri */}
              {mealPricesLoaded && nights > 0 && (
                <div style={{ background: '#f0f8ff', padding: '1rem', borderRadius: '8px', marginTop: '1rem', marginBottom: '1rem', border: '1px solid #d0e8ff' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>🍽️ Yemek Seçenekleri</h4>
                  
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={includeBreakfast} onChange={(e) => setIncludeBreakfast(e.target.checked)} style={{ cursor: 'pointer', marginTop: '4px' }} />
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>🍳 Kahvaltı Ekle</div>
                        <div style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: '600' }}>
                          Yetişkin: ₺{mealPrices.breakfastAdult} | 7-12 yaş: ₺{mealPrices.breakfastChild7_12} | 0-6 yaş: ₺{mealPrices.breakfastChild0_6}
                        </div>
                      </div>
                    </label>
                    {includeBreakfast && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '6px', fontSize: '0.85rem' }}>
                        {billableBreakfastNights < nights ? (
                          <span>☕ {billableBreakfastNights}/{nights} gece <span style={{ color: '#f57c00' }}>(kapalı gün: {excludedNights.breakfasts} gece düşüldü)</span> = <strong>₺{mealCosts.breakfast.toLocaleString('tr-TR')}</strong></span>
                        ) : (
                          <span>☕ {nights} gece = <strong>₺{mealCosts.breakfast.toLocaleString('tr-TR')}</strong></span>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={includeDinner} onChange={(e) => setIncludeDinner(e.target.checked)} style={{ cursor: 'pointer', marginTop: '4px' }} />
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>🍖 Akşam Yemeği Ekle</div>
                        <div style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: '600' }}>
                          Yetişkin: ₺{mealPrices.dinnerAdult} | 7-12 yaş: ₺{mealPrices.dinnerChild7_12} | 0-6 yaş: ₺{mealPrices.dinnerChild0_6}
                        </div>
                      </div>
                    </label>
                    {includeDinner && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '6px', fontSize: '0.85rem' }}>
                        {billableDinnerNights < nights ? (
                          <span>🍽️ {billableDinnerNights}/{nights} gece <span style={{ color: '#f57c00' }}>(kapalı gün: {excludedNights.dinners} gece düşüldü)</span> = <strong>₺{mealCosts.dinner.toLocaleString('tr-TR')}</strong></span>
                        ) : (
                          <span>🍽️ {nights} gece = <strong>₺{mealCosts.dinner.toLocaleString('tr-TR')}</strong></span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Toplam Fiyat Özeti */}
              {(includeBreakfast || includeDinner) && (
                <div style={{ background: '#fff8e1', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #ffe082' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                    <span>🏨 Oda</span><span>₺{(selectedRoom.price || 0).toLocaleString('tr-TR')}</span>
                  </div>
                  {includeBreakfast && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      <span>🍳 Kahvaltı</span><span>₺{mealCosts.breakfast.toLocaleString('tr-TR')}</span>
                    </div>
                  )}
                  {includeDinner && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                      <span>🍖 Akşam Yemeği</span><span>₺{mealCosts.dinner.toLocaleString('tr-TR')}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid #ffe082', fontWeight: 'bold', fontSize: '1.1rem', color: '#d4af37' }}>
                    <span>TOPLAM</span><span>₺{((selectedRoom.price || 0) + mealCosts.total).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleBooking} style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input type="text" name="guestName" className="form-input" required />
                </div>
                <div className="form-group">
                  <label>E-posta *</label>
                  <input type="email" name="guestEmail" className="form-input" required />
                </div>
                <div className="form-group">
                  <label>Telefon *</label>
                  <input 
                    type="tel" 
                    name="guestPhone" 
                    className="form-input" 
                    required 
                    placeholder="05XX XXX XX XX" 
                    maxLength={20}
                    value={phoneInput}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9+\s]/g, '');
                      setPhoneInput(value);
                      if (value.length > 0) {
                        const validation = validatePhone(value);
                        setPhoneError(validation.valid ? '' : validation.error || '');
                      } else {
                        setPhoneError('');
                      }
                    }}
                    style={{ borderColor: phoneError ? '#dc3545' : undefined }}
                  />
                  {phoneError && <div style={{ color: '#dc3545', fontSize: '0.85rem', marginTop: '0.25rem' }}>{phoneError}</div>}
                </div>
                <div className="form-group">
                  <label>Özel İstekler</label>
                  <textarea name="specialRequests" className="form-input" rows={3}></textarea>
                </div>
                
                {/* Fiyat Yükleme Hatası Uyarısı */}
                {mealPriceError && (
                  <div style={{ 
                    padding: '1rem', 
                    background: '#fff3cd', 
                    border: '2px solid #ff9800',
                    borderRadius: '8px',
                    color: '#856404',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginTop: '1rem'
                  }}>
                    ⚠️ Fiyat bilgileri yüklenemedi. Rezervasyon şu anda yapılamıyor. 
                    Lütfen sayfayı yenileyip tekrar deneyin.
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn-primary" disabled={loading || mealPriceError} style={{ flex: 1 }}>
                    {loading ? 'Gönderiliyor...' : `Rezervasyon Yap${(includeBreakfast || includeDinner) ? ` (₺${((selectedRoom.price || 0) + mealCosts.total).toLocaleString('tr-TR')})` : ''}`}
                  </button>
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => { setShowBookingForm(false); setIncludeBreakfast(false); setIncludeDinner(false); }}
                    style={{ flex: 1 }}
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Odalar Listesi */}
        {searched && availableRooms.length > 0 && (
          <div className="rooms-grid">
            {availableRooms.map(room => (
            <div key={room.id || room.roomTypeId} className="room-card">
              <div className="room-image" style={room.image ? { backgroundImage: `url(${room.image})` } : {}}>
                <div className="room-number">{room.roomTypeName || room.name}</div>
                
                {/* Fiyat Her Zaman Görünür - Gradient Üzerinde */}
                <div className="room-price">
                  <strong>
                    {room.price 
                      ? `${room.price.toLocaleString('tr-TR')} TL` 
                      : '10.000 TL'}
                  </strong>
                  <span>
                    {room.pricePerNight && room.nights 
                      ? `${room.pricePerNight.toLocaleString('tr-TR')} TL/gece × ${room.nights} gece`
                      : 'HER ŞEY DAHİL • 2 gece'}
                  </span>
                </div>
              </div>
              <div className="room-content">
                <h3>{room.roomTypeName || room.name}</h3>
                <p>{room.description}</p>

                <ul className="room-features">
                  {room.features && room.features.length > 0 ? (
                    room.features.map((feature: string, index: number) => (
                      <li key={index}>✓ {feature}</li>
                    ))
                  ) : (
                    <>
                      {room.maxOccupancy && <li>👥 {room.maxOccupancy} Kişi</li>}
                      {room.maxAdults && <li>👨 {room.maxAdults} Yetişkin</li>}
                      {room.maxChildren && <li>👶 {room.maxChildren} Çocuk</li>}
                      {room.viewType && (
                        <li>
                          {room.viewType === 'sea' ? '🌊 Deniz Manzarası' : 
                           room.viewType === 'garden' ? '🌳 Bahçe Manzarası' : 
                           '🏠 Standart Manzara'}
                        </li>
                      )}
                      {room.floor && <li>🏢 {room.floor}. Kat</li>}
                    </>
                  )}
                  {/* Ek yatak uyarı badge */}
                  {(Number(adultsCount) + Number(childrenCount)) > (room.standardCapacity || (room.maxOccupancy ? room.maxOccupancy - 1 : 3)) && (
                    <li style={{ color: '#856404', fontWeight: '600', background: '#fff3cd', padding: '0.5rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                      ⚠️ Ek yatak açılacak (konfor sınırlı)
                    </li>
                  )}
                </ul>

                <button 
                  className="btn-details" 
                  onClick={() => handleRoomSelect(room)}
                  disabled={loading}
                >
                  {loading ? 'Yükleniyor...' : 'Rezervasyon Yap'}
                </button>
              </div>
            </div>
            ))}
          </div>
        )}

        {/* Arama yapılmadı mesajı */}
        {!searched && !loading && (
          <div className="no-results">
            <p>Müsait odaları görmek için yukarıdan tarih seçip arama yapınız.</p>
          </div>
        )}

        {/* Arama yapıldı ama sonuç yok */}
        {searched && availableRooms.length === 0 && !loading && !error && (
          <div className="no-results">
            <p>Seçilen tarihler için müsait oda bulunamadı.</p>
            <p style={{ fontSize: '0.9rem', color: '#888' }}>Farklı tarihler deneyebilirsiniz.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomsPage;
